// RFQ Public Response — Tedarikçi kendi token'lı linkten teklif gönderir. Auth gerekmez.
// URL: /rfq-public/<token>
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// API base — kullanıcı public endpoint kullanıyor, auth-aware api.js'i değil
const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '') + '/api/rfq/public';

export default function RfqPublicResponsePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({}); // { rfq_item_id: { unit_price, lead_time_days, notes } }
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API_BASE + '/' + token);
      if (r.data.success) {
        setData(r.data.data);
        const existing = {};
        (r.data.data.items || []).forEach(it => {
          if (it.response) existing[it.id] = {
            unit_price: it.response.unit_price,
            lead_time_days: it.response.lead_time_days || '',
            notes: it.response.notes || '',
          };
        });
        setResponses(existing);
        if (r.data.data.response_status === 'responded') setSubmitted(true);
      } else {
        setError(r.data.message);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Yükleme hatası');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setItemField = (itemId, field, val) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: val },
    }));
  };

  const total = data?.items?.reduce((sum, it) => {
    const p = parseFloat(responses[it.id]?.unit_price) || 0;
    return sum + (p * parseFloat(it.quantity));
  }, 0) || 0;

  const submit = async () => {
    if (!data?.items?.some(it => parseFloat(responses[it.id]?.unit_price) > 0)) {
      alert('En az bir kaleme fiyat girin');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        responses: data.items.map(it => ({
          rfq_item_id: it.id,
          unit_price: responses[it.id]?.unit_price,
          lead_time_days: responses[it.id]?.lead_time_days,
          notes: responses[it.id]?.notes,
        })),
        notes,
        total_amount: total,
        currency_code: data.currency_code,
      };
      const r = await axios.post(API_BASE + '/' + token + '/respond', payload);
      if (r.data.success) {
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        alert(r.data.message || 'Hata');
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Gönderim hatası');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={S.center}><h2>Yükleniyor...</h2></div>;
  if (error) return <div style={S.center}><h2 style={{ color: '#dc2626' }}>{error}</h2></div>;
  if (!data) return null;

  return (
    <div style={S.wrap}>
      <div style={S.container}>
        <div style={S.header}>
          <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'monospace' }}>{data.rfq_number}</div>
          <h1 style={{ margin: '4px 0 8px' }}>{data.title}</h1>
          <div style={{ color: '#5a6a7e', fontSize: 14 }}>
            <strong>{data.company_name}</strong> · {data.vendor_title} tarafından yanıtlanacak
          </div>
          {data.due_date && (
            <div style={{ marginTop: 8, padding: '6px 10px', background: '#fef3c7', borderRadius: 6, display: 'inline-block', fontSize: 13 }}>
              ⏰ Son yanıt: <strong>
                {new Date(data.due_date).toLocaleDateString('tr-TR')}
                {data.due_time && ' · ' + String(data.due_time).substring(11, 16)}
              </strong>
            </div>
          )}
          {data.response_status === 'responded' && (
            <div style={{ marginTop: 8, padding: '6px 10px', background: '#dbeafe', borderRadius: 6, display: 'inline-block', fontSize: 13, marginLeft: 8 }}>
              📩 Teklifiniz alındı: <strong>{data.responded_at && new Date(data.responded_at).toLocaleString('tr-TR')}</strong>
              {data.last_modified_at && (
                <span style={{ marginLeft: 8, color: '#a16207' }}>
                  · ✏️ Son düzeltme: <strong>{new Date(data.last_modified_at).toLocaleString('tr-TR')}</strong>
                  {data.revision_count > 0 && ` (${data.revision_count} kez)`}
                </span>
              )}
            </div>
          )}
        </div>

        {submitted ? (
          <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 60 }}>✅</div>
            <h2 style={{ margin: '16px 0 8px', color: '#16a34a' }}>Teklifiniz alındı!</h2>
            <p style={{ color: '#5a6a7e' }}>
              <strong>{data.company_name}</strong> tarafından değerlendirilecek. Bir geri dönüş yapılacaktır.
            </p>
            <button onClick={() => setSubmitted(false)} style={{ ...S.btnGhost, marginTop: 20 }}>Yanıtı Düzenle</button>
          </div>
        ) : (
          <>
            {data.description && (
              <div style={{ ...S.card, marginBottom: 14 }}>
                <strong>Açıklama:</strong> {data.description}
              </div>
            )}

            <div style={S.card}>
              <h3 style={{ marginTop: 0 }}>📦 Teklif Vermeniz İstenen Kalemler</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f4f6fa', textAlign: 'left' }}>
                    <th style={S.th}>Açıklama</th>
                    <th style={{ ...S.th, textAlign: 'right', width: 90 }}>Miktar</th>
                    <th style={{ ...S.th, width: 140 }}>Birim Fiyat ({data.currency_code})</th>
                    <th style={{ ...S.th, width: 100 }}>Teslim (gün)</th>
                    <th style={{ ...S.th, textAlign: 'right', width: 130 }}>Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(it => {
                    const p = parseFloat(responses[it.id]?.unit_price) || 0;
                    const lineTotal = p * parseFloat(it.quantity);
                    return (
                      <tr key={it.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={S.td}><strong>{it.description}</strong></td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{it.quantity} {it.unit_code || ''}</td>
                        <td style={S.td}>
                          <input type="number" step="0.01" value={responses[it.id]?.unit_price || ''}
                            onChange={e => setItemField(it.id, 'unit_price', e.target.value)}
                            placeholder="0.00" style={S.input} />
                        </td>
                        <td style={S.td}>
                          <input type="number" value={responses[it.id]?.lead_time_days || ''}
                            onChange={e => setItemField(it.id, 'lead_time_days', e.target.value)}
                            placeholder="—" style={S.input} />
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>
                          {lineTotal > 0 ? lineTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f4f6fa', fontWeight: 700, fontSize: 16 }}>
                    <td colSpan={4} style={{ ...S.td, textAlign: 'right' }}>TOPLAM:</td>
                    <td style={{ ...S.td, textAlign: 'right', color: '#1d6ef5' }}>
                      {total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {data.currency_code}
                    </td>
                  </tr>
                </tfoot>
              </table>

              <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600 }}>Genel Not (opsiyonel)</label>
                <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Ödeme koşulları, garanti, ek hizmetler vb."
                  style={{ ...S.input, width: '100%', fontFamily: 'inherit' }} />
              </div>

              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={submit} disabled={submitting} style={S.btnPrimary}>
                  {submitting ? 'Gönderiliyor...' : '📨 Teklifi Gönder'}
                </button>
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              Bu sayfa <strong>{data.company_name}</strong> tarafından gönderildi · hent ERP
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  wrap: { minHeight: '100vh', background: '#f0f2f5', padding: 20, fontFamily: 'system-ui, sans-serif' },
  container: { maxWidth: 980, margin: '0 auto' },
  header: { background: '#fff', padding: 24, borderRadius: 10, marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  card: { background: '#fff', padding: 18, borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'system-ui' },
  th: { padding: '10px 8px', fontSize: 12, fontWeight: 600, color: '#5a6a7e', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '8px' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d9e0', borderRadius: 6, fontSize: 14 },
  btnPrimary: { padding: '12px 24px', background: '#1d6ef5', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 15 },
  btnGhost: { padding: '8px 16px', background: 'transparent', border: '1px solid #d1d9e0', borderRadius: 6, cursor: 'pointer' },
};
